import { Injectable } from '@angular/core';
import { Yii } from 'libraries/interfaces/Yii';
import {
    PersonRole,
    TravellerXTitle,
    TravellerXType
} from 'libraries/service/resources/booking/interfaces/get-bookings.enum';
import { ICustomer, IHolder, ITraveller } from 'libraries/service/resources/booking/interfaces/IGetBookings';
import { LabelService } from "libraries/service/label/label.service";
import { TravellerXTypeAbbreviation } from "libraries/service/resources/shared/enums/traveller.enum";
import { ErrorTracingService, TraceSeverity } from "../error-tracing/error-tracing.service";
import { IconDefinition } from "@fortawesome/free-regular-svg-icons";
import { faFemale, faMale, faUser } from "@fortawesome/free-solid-svg-icons";
import { ISamContact } from 'libraries/interfaces/IUser';

@Injectable()
export class TravellerDataTranslationService {

    constructor(
        private readonly labelService: LabelService,
        private readonly errorTracingService: ErrorTracingService) {
    }

    public translateType(xType: TravellerXType, abbreviate: boolean = false): string {
        switch (xType.charAt(0).toLowerCase() + xType.slice(1)) {
            case TravellerXType.adult:

            case TravellerXType.person:
            case TravellerXType.companionPrimary:
            case TravellerXType.companionSecondary:
                return abbreviate ? TravellerXTypeAbbreviation.ADT : Yii.t('wui-shared-js', 'adulte');
            case TravellerXType.visitingFriends:
                return abbreviate ? TravellerXTypeAbbreviation.ADT : Yii.t('wui-shared-js', 'adulte VFR');
            case TravellerXType.child:
                return abbreviate ? TravellerXTypeAbbreviation.CHD : Yii.t('wui-shared-js', 'enfant');
             case TravellerXType.visitingFriendsChild:
                return abbreviate ? TravellerXTypeAbbreviation.CHD : Yii.t('wui-shared-js', 'enfant de VFR');
            case TravellerXType.infant:
                return abbreviate ? TravellerXTypeAbbreviation.INF : Yii.t('wui-shared-js', 'bébé');
            case TravellerXType.visitingFriendsInfant:
                return abbreviate ? TravellerXTypeAbbreviation.INF : Yii.t('wui-shared-js', 'bébé de VFR');
            case TravellerXType.unaccompaniedMinor:
                return Yii.t('wui-shared-js', 'mineur non accompagné');
            case TravellerXType.worker:
                return Yii.t('wui-shared-js', 'travailleur');
            case TravellerXType.workerChild:
                return Yii.t('wui-shared-js', 'enfant de travailleur');
            case TravellerXType.workerInfant:
                return Yii.t('wui-shared-js', 'bébé de travailleur');
            case TravellerXType.youth:
                return Yii.t('wui-shared-js', 'jeune');
             case TravellerXType.student:
                return Yii.t('wui-shared-js', 'étudiant');
             case TravellerXType.senior:
                return Yii.t('wui-shared-js', 'senior');
            default:
                const errorMessage: string = Yii.t('wui-shared-js', 'type inconnu');
                return errorMessage;
        }
    }

    public translateTitle(xTitle: TravellerXTitle | string): string {
        switch (xTitle.toLowerCase()) {
            case TravellerXTitle.mr:
                return `${Yii.t('wui-shared-js', 'M')}.`;
            case TravellerXTitle.ms:
            case TravellerXTitle.mrs:
                return `${Yii.t('wui-shared-js', 'Mme')}.`;
            case TravellerXTitle.in:
            case TravellerXTitle.ch:
                return "";
            default:
                const errorMessage: string = Yii.t('wui-shared-js', 'xTitle inconnu');
                this.errorTracingService.captureMessage(`${errorMessage} : ${xTitle}`, TraceSeverity.Warning);
                return "";
        }
    }

    public getGivenName(givenName: string[]): string {
        return givenName
            .map(capitalize)
            .join(', ');
    }

    public getTravellerIdentity(traveller: ITraveller | IHolder | ICustomer | ISamContact): string {
        const title: string = traveller.xTitle || traveller.title ? this.translateTitle(traveller.xTitle || traveller.title) : '';
        const lastName: string = traveller.lastName ? traveller.lastName.toUpperCase() : '';
        const firstName: string = traveller.firstName ? traveller.firstName : '';
        const travellerData: ITraveller = traveller as ITraveller;
        if (lastName === "" && firstName === "" && travellerData.xId) {
            return travellerData.xId;
        }
        return `${title} ${lastName} ${firstName}`;
    }

    public getTravellerAge(traveller: ITraveller, ageDate?: string): string {
        if (traveller.birthDate) {
            const stopDate: Date = ageDate ?
                new Date(ageDate) :
                new Date();
            const birthDate: Date = new Date(traveller.birthDate);
            let yearsDifference: number = stopDate.getFullYear() - birthDate.getFullYear();
            if (
                stopDate.getMonth() < birthDate.getMonth() ||
                (stopDate.getMonth() === birthDate.getMonth() && stopDate.getDate() < birthDate.getDate())
            ) {
                yearsDifference--;
            }
            switch (yearsDifference) {
                case 0:
                    return Yii.t('wui-shared-js', "moins d'1 an");
                case 1:
                    return Yii.t('wui-shared-js', "1 an");
                default:
                    return this.labelService.completeLabel(
                        Yii.t('wui-shared-js', '%placeholder% ans'),
                        yearsDifference.toString()
                    );
            }
        }
        return null;
    }

    public getInitials(traveller: ITraveller | IHolder | ICustomer, index: number): string {
        if (hasName(traveller)) {
            const lastName: string = traveller.lastName ? `${traveller.lastName.charAt(0)}.` : '';
            const firstName: string = traveller.firstName ? `${traveller.firstName.charAt(0)}.` : '';
            return `${lastName}${firstName}`.toUpperCase();
        }
        const travellerWording: string = Yii.t('wui-shared-js', 'Voyageur');
        if (index) {
            return `${travellerWording.charAt(0)}${index}`;
        }
        return `${travellerWording.charAt(0)}`;
    }

    public translateRole(role: PersonRole): string {
        switch (role) {
            case PersonRole.traveller:
                return Yii.t('wui-shared-js', 'Voyageur');
            case PersonRole.customer:
                return Yii.t('wui-shared-js', 'Client');
            case PersonRole.agency:
                return Yii.t('wui-shared-js', 'Agence');
            default:
                return '';
        }
    }

    public translateTitleInIconDefinition(title: TravellerXTitle): IconDefinition {
        switch (title.toLowerCase()) {
            case TravellerXTitle.mr:
                return faMale;
            case TravellerXTitle.ms:
            case TravellerXTitle.mrs:
                return faFemale;
            case TravellerXTitle.in:
            case TravellerXTitle.ch:
            default:
                return faUser;
        }
    }
}

function capitalize(s: string): string {
    return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
}

function hasName(traveller: ITraveller | IHolder | ICustomer): boolean {
    return Boolean(traveller.lastName && traveller.firstName);
}
